import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Plus,
  Settings,
  CreditCard,
  Package,
  LayoutGrid,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  User,
  LogOut,
  Users,
  Shield
} from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";

const PortalLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [menuReady, setMenuReady] = useState(false);
  const { logout, isAdmin, isLoading, profile } = useAuth();

  // Set menu ready immediately when profile loads and auth is complete
  useEffect(() => {
    if (profile && !isLoading) {
      setMenuReady(true);
    }
  }, [profile, isLoading]);
  
  // Fallback timer to ensure menu becomes ready
  useEffect(() => {
    const timer = setTimeout(() => {
      setMenuReady(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Auth state debugging removed to prevent infinite re-renders

  // Regular user menu items
  const userMenuItems = [
    {
      icon: MessageSquare,
      label: "Chatbots",
      href: "/portal",
    },
    {
      icon: CreditCard,
      label: "Billing",
      href: "/settings/billing",
    },
    {
      icon: Settings,
      label: "Settings",
      href: "/portal/settings",
    },
    {
      icon: User,
      label: "Profile",
      href: "/portal/profile",
    },
    {
      icon: LogOut,
      label: "Logout",
      onClick: () => {}, // Will be handled specially
      isLogout: true,
    },
  ];

  // Admin menu items (only shown to admins)
  const adminMenuItems = [
    {
      type: 'separator' as const,
      label: 'Admin Panel'
    },
    {
      icon: Users,
      label: "Users",
      href: "/admin/users",
    },
    {
      icon: Settings,
      label: "AI Settings",
      href: "/admin/ai-settings",
    },
    {
      icon: Package,
      label: "Plans",
      href: "/admin/plans",
    },
  ];

  // Combine menu items based on user role - strict admin check
  // Only show admin menu items if user is explicitly verified as admin
  const shouldShowAdminMenu = menuReady && isAdmin && profile?.role === 'admin';
  const allMenuItems = shouldShowAdminMenu 
    ? [...userMenuItems, ...adminMenuItems] 
    : userMenuItems;
  
  const handleLogout = async () => {
    const { success, error } = await logout();
    if (!success) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-gray-50">
      <div className="flex flex-1">
        <div
          className={cn(
            "border-r bg-white transition-all duration-300 ease-in-out relative",
            isCollapsed ? "w-12" : "w-48"
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute -right-3 top-6 h-6 w-6 rounded-full border bg-white shadow-md"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>

          <div className="p-3">
            <div className={cn(
              "flex items-center gap-2 mb-6",
              isCollapsed ? "justify-center" : "px-2"
            )}>
              <MessageSquare className="h-5 w-5 text-primary flex-shrink-0" />
              {!isCollapsed && <span className="font-bold text-lg">OfferChat</span>}
            </div>

            <nav className="space-y-1">
              {allMenuItems.map((item, index) => {
                // Handle separator items
                if ('type' in item && item.type === 'separator') {
                  return (
                    <div key={`separator-${index}`} className="py-2">
                      {!isCollapsed && (
                        <>
                          <div className="border-t border-gray-200 my-2" />
                          <div className="px-3 py-1">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              {item.label}
                            </p>
                          </div>
                        </>
                      )}
                      {isCollapsed && (
                        <div className="border-t border-gray-200 my-2" />
                      )}
                    </div>
                  );
                }
                
                // Handle logout item specially
                if ('isLogout' in item && item.isLogout) {
                  return (
                    <Button
                      key={`logout-${index}`}
                      variant="ghost"
                      className={cn(
                        "w-full",
                        "text-gray-700 hover:bg-gray-100",
                        isCollapsed ? "justify-center px-1" : "justify-start text-sm"
                      )}
                      onClick={handleLogout}
                    >
                      <item.icon className={cn(
                        "h-4 w-4",
                        !isCollapsed && "mr-2"
                      )} />
                      {!isCollapsed && <span>{item.label}</span>}
                    </Button>
                  );
                }
                
                return (
                  <Link key={item.href} to={item.href}>
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full",
                        location.pathname === item.href
                          ? "bg-primary/10 text-primary"
                          : "text-gray-700 hover:bg-gray-100",
                        isCollapsed ? "justify-center px-1" : "justify-start text-sm"
                      )}
                    >
                      <item.icon className={cn(
                        "h-4 w-4",
                        !isCollapsed && "mr-2"
                      )} />
                      {!isCollapsed && <span>{item.label}</span>}
                    </Button>
                  </Link>
                );
              })}
              

            </nav>
          </div>
        </div>

        <div className="flex-1">
          {children}
        </div>
      </div>
      
      <div className="border-t bg-gray-50">
        <div className="container mx-auto px-6 py-2">
          <div className="flex justify-between items-center text-xs text-gray-500">
            <p> {new Date().getFullYear()} OfferChat</p>
            <Link to="/" className="hover:text-primary">Back to Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalLayout;
